import cn from 'classnames';
import { FC, useEffect, useState, Fragment } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import axios from 'axios';

import { Button } from '@/components/buttons/Button';
import { InputBox } from '@/components/forms/InputBox';
import { Input } from '@/components/inputs/Input';
import { MyCheckbox } from '@/components/forms/Checkbox';

import { Field, FieldSet, IFormData, NAMES } from './Form.props';

import formDataStatic from '@/data/formData.json';
import { Heading } from '@/components/typography';
import { AnimatedCard } from '@/components/animation/AnimatedCard';

export const Form: FC = (): JSX.Element => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    clearErrors,
    reset,

    // formState: { errors, isValid },
    // setValue: setFormValue,
    // trigger,
    // getFieldState,
    // getValues,
    // setError,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<any>({
    criteriaMode: 'all',
    mode: 'onChange',
  });

  const [language, setLanguage] = useState<'EN' | 'UA'>('UA');

  const router = useRouter();

  useEffect(() => {
    router.locale == 'en' ? setLanguage('EN') : setLanguage('UA');
    reset();
  }, [reset, router]);

  const formDataValues = formDataStatic[language];

  const formConfirm = watch('formConfirm');

  const onSubmit: SubmitHandler<IFormData> = async data => {
    try {
      axios.defaults.headers.post['Content-Type'] = 'application/json';
      // mail - pharmacovigilance@microkhim.com
      const responseSend = await axios.post(
        'https://formsubmit.co/ajax/f718f35641e2ec70c2e37424dc1c012b',
        {
          ['Назва форми']: 'ФАРМАКОНАГЛЯД',
          [`Ім'я репортера`]: data.formReporterName,
          [`Статус репортера`]: data.formReporterStatus,
          [`Телефон репортера`]: data.formReporterPhone,
          [`Email репортера`]: data.formReporterEmail,
          [`Ім'я пацієнта`]: data.formPatientName,
          [`Вік пацієнта`]: data.formPatientAge,
          [`Стать пацієнта`]: data.formPatientGender,
          [`Назва ЛЗ`]: data.formDrugName,
          [`Виробник ЛЗ`]: data.formDrugManufacturer,
          [`Форма ЛЗ`]: data.formDrugMedicalForm,
          [`Серійний номер ЛЗ`]: data.formDrugSerial,
          [`Додаткові відомості`]: data.formReviewMessage,
        },
      );

      if (responseSend.status == 200) {
        reset();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formFieldSetsRenderer = (fieldSets: FieldSet[]) => {
    return fieldSets.map((item: FieldSet) => {
      return (
        <Fragment key={item.title}>
          <fieldset
            className={cn(
              'grid gap-x-8 gap-y-16 pt-[88px] md:gap-y-20 md:pt-[116px] lg:relative lg:px-[104px]',
              item.fields.length > 1 ? 'md:grid-cols-2' : 'grid-cols-1',
            )}
          >
            <legend className="lg:absolute lg:left-0 lg:top-0">
              <Heading tag="h3" content={item.title} />
            </legend>
            {item.fields.map((item: Field, index: number) => {
              const {
                type = 'text',
                label = '',
                name,
                placeholder = '',
                required = false,
              } = item;
              const isEven = index % 2 === 0;
              return (
                <AnimatedCard
                  key={name}
                  initial={{ x: isEven ? '-100%' : '100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  delay={isEven ? 0 : 0.2}
                >
                  <InputBox
                    label={label}
                    order={index + 1}
                    isNecessary={required}
                    errorMsg={errors[NAMES[name]]?.message}
                  >
                    <Input
                      required={required}
                      type={type}
                      name={NAMES[name]}
                      register={register}
                      placeholder={placeholder}
                      onChange={() => clearErrors(name)}
                      locale={language}
                    />
                  </InputBox>
                </AnimatedCard>
              );
            })}
          </fieldset>
        </Fragment>
      );
    });
  };

  return (
    <form
      className="w-full overflow-x-hidden"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="on"
    >
      {formDataValues?.title && (
        <Heading tag="h2" withDivider content={formDataValues.title} />
      )}
      <div className="mb-8 flex flex-col gap-[100px] md:mb-10">
        {formDataValues?.fieldSets.length > 0 &&
          formFieldSetsRenderer(formDataValues?.fieldSets as FieldSet[])}
      </div>
      {formDataValues?.checkBox.length > 0 && (
        <MyCheckbox
          name={formDataValues?.checkBox[0]?.name as string}
          control={control}
          label={formDataValues?.checkBox[0]?.label}
        />
      )}
      {formDataValues?.submit && (
        <Button
          type="submit"
          className="mx-auto"
          withIcon
          disabled={!formConfirm}
          title={formDataValues?.submit?.title}
          id="fm"
        >
          {formDataValues?.submit?.label}
        </Button>
      )}
    </form>
  );
};
